import { useState } from 'react';
import { formatBytes, formatTime } from '../../media/editor-utils';
import { useStudio } from '../../store/studio-store';

export function AssetsPanel() {
  const assets = useStudio((s) => s.assets);
  const tracks = useStudio((s) => s.tracks);
  const importFiles = useStudio((s) => s.importFiles);
  const removeAsset = useStudio((s) => s.removeAsset);
  const addClip = useStudio((s) => s.addClip);
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);

  async function handleFiles(files: FileList | File[]) {
    setImporting(true);
    try {
      await importFiles(files);
    } finally {
      setImporting(false);
    }
  }

  function addToTimeline(assetId: string, kind: string) {
    const track = tracks.find((t) => t.kind === kind);
    if (!track) return;
    const lastEnd = track.clips.reduce((max, c) => Math.max(max, c.start + c.duration), 0);
    addClip(assetId, track.id, lastEnd);
  }

  const targetTrackKind = (kind: string) => (kind === 'audio' ? 'audio' : 'video');

  return (
    <div className="panel">
      <div className="panel-header">
        <h2>Assets</h2>
      </div>

      <section className="panel-section">
        <label
          className={`drop-zone${dragging ? ' dragging' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            void handleFiles(e.dataTransfer.files);
          }}
        >
          <input
            type="file"
            multiple
            accept="image/*,video/*,audio/*,.glb,.gltf"
            onChange={(e) => {
              if (e.target.files) void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <span className="drop-title">{importing ? 'Importing…' : 'Drop media here'}</span>
          <span className="hint">Images, videos and audio — or click to browse</span>
        </label>
      </section>

      {assets.length === 0 ? (
        <div className="panel-empty">
          <p>No assets yet.</p>
          <p className="hint">Import media, then drag it onto the timeline below.</p>
        </div>
      ) : (
        <section className="panel-section">
          <h3>Imported ({assets.length})</h3>
          <div className="asset-grid">
            {assets.map((asset) => (
              <div key={asset.id} className="asset-card">
                <div className="asset-thumb">
                  {asset.kind === 'audio' ? (
                    <span className="asset-audio-icon">♫</span>
                  ) : asset.kind === 'glb' || asset.kind === 'gltf' ? (
                    <span className="asset-audio-icon model-badge">🧊</span>
                  ) : asset.thumbUrl || asset.url ? (
                    <img
                      src={asset.thumbUrl ?? asset.url ?? undefined}
                      alt={asset.name}
                      className="asset-img"
                    />
                  ) : (
                    <span className="asset-audio-icon">?</span>
                  )}
                </div>
                <div className="asset-type">{asset.kind}</div>
                <div className="asset-name" title={asset.name}>
                  {asset.name}
                </div>
                <div className="asset-meta">
                  {asset.width && asset.height ? `${asset.width}×${asset.height} · ` : ''}
                  {asset.duration ? `${formatTime(asset.duration)} · ` : ''}
                  {formatBytes(asset.size)}
                </div>
                <div className="asset-actions">
                  <button
                    type="button"
                    className="primary-btn asset-add"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('application/x-asset-id', asset.id);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => addToTimeline(asset.id, targetTrackKind(asset.kind))}
                  >
                    Add to timeline
                  </button>
                  <button
                    type="button"
                    className="ghost-btn asset-delete"
                    onClick={() => removeAsset(asset.id)}
                    aria-label={`Remove ${asset.name}`}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="hint">Drag an asset card onto a track in the timeline to place it.</p>
        </section>
      )}
    </div>
  );
}
